/**
 * Centralised wrapper around the browser `fetch` API for the Ancalagon backend.
 *
 * Every authenticated and public backend HTTP call in the app routes through
 * this single chokepoint. It handles base-URL prefixing, the Authorization
 * header, a default Content-Type for requests that carry a body, and the
 * X-Impersonate-Client-ID header when client impersonation is active.
 *
 * The /impersonation/* paths are exempt from the impersonation header — those
 * endpoints always reflect the real authenticated user's permissions.
 *
 * Callers continue to handle `.json()`, `response.ok`, status codes, and JSend
 * status checks themselves — the helper deliberately returns the raw `Response`
 * unchanged.
 */

const IMPERSONATION_PATHS_PREFIX = '/impersonation/';

export interface ApiFetchOptions {
  /** OAuth access token. Required for authenticated endpoints. Pass null for public endpoints. */
  token: string | null;

  /**
   * Client ID to impersonate. Sends X-Impersonate-Client-ID header when set
   * (and the path is not under /impersonation/*). Pass null when no
   * impersonation is active.
   */
  impersonatedClientID?: number | null;

  /** Standard fetch options (method, body, etc.) */
  method?: string;
  body?: BodyInit | null;
  headers?: HeadersInit;
  signal?: AbortSignal;

  /**
   * Browser HTTP-cache mode. Pass 'no-store' for responses that must never
   * touch the on-disk cache (e.g. the certificate download endpoint when it
   * relays private key material — RFC 9111 only forbids *shared* caches from
   * storing Authorization-bearing responses; the browser's private cache may
   * store them unless told otherwise).
   */
  cache?: RequestCache;
}

export async function apiFetch(path: string, options: ApiFetchOptions): Promise<Response> {
  if (path.startsWith('http')) {
    throw new Error('apiFetch expects a path starting with /, not a full URL');
  }
  if (!path.startsWith('/')) {
    throw new Error(`apiFetch expects a path starting with /, got: ${path}`);
  }

  const baseUrl = process.env.REACT_APP_BACKENDAPI_URL;
  if (!baseUrl) {
    throw new Error('REACT_APP_BACKENDAPI_URL is not configured');
  }

  const headers = new Headers(options.headers ?? {});

  if (options.token) {
    headers.set('Authorization', `Bearer ${options.token}`);
  }

  if (options.body !== undefined && options.body !== null && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  // Impersonation header.
  // The /impersonation/* paths are exempt — the backend silently ignores the
  // header on those paths, but stripping it client-side too keeps the contract
  // symmetric and makes the rule obvious to readers.
  if (
    options.impersonatedClientID &&
    options.impersonatedClientID > 0 &&
    !path.startsWith(IMPERSONATION_PATHS_PREFIX)
  ) {
    headers.set('X-Impersonate-Client-ID', String(options.impersonatedClientID));
  }

  return fetch(`${baseUrl}${path}`, {
    method: options.method,
    body: options.body,
    headers,
    signal: options.signal,
    cache: options.cache,
  });
}
